import React from 'react';
import { motion } from 'framer-motion';
import { Github } from 'lucide-react';
import PageLayout from '../layout/PageLayout';
import { team, specialThanks } from '../../data/team';

const AboutPage: React.FC = () => {
  const faqItems = [
    {
      q: 'You have a single project and you made a whole website for it?',
      a: 'Yeah, I know. I was the one that did it.'
    },
    {
      q: 'Are you gonna make anything else?',
      a: 'Probably. Maybe. We\'ll see. No promises.'
    },
    {
      q: 'Why do you have a website?',
      a: 'Because we fucking can.'
    },
    {
      q: 'This is really professional.',
      a: 'We are not professionals. We are weebs. There\'s a difference.'
    },
    {
      q: 'I found the secret themes!',
      a: 'That\'s not a question, but good job! There are 5 total. Keep exploring.'
    }
  ];

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-theme font-bold mb-6 text-gradient">
              Who We Are
            </h1>
            <p className="text-xl md:text-2xl mb-4" style={{ color: 'var(--color-text)' }}>
              (And Why We're Like This)
            </p>
            <p className="text-lg" style={{ color: 'var(--color-text-secondary)' }}>
              We're weebs who code. That's it. That's the explanation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-theme font-bold mb-12 text-center text-gradient">
              The Team
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              {team.map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 rounded-xl"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-theme font-bold" style={{ color: 'var(--color-primary)' }}>
                        {member.name}
                      </h3>
                      <p className="text-sm font-semibold mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                        {member.title}
                      </p>
                      <p className="text-sm italic mt-1" style={{ color: 'var(--color-accent)' }}>
                        "{member.weebTitle}"
                      </p>
                    </div>
                    {member.github && (
                      <a
                        href={member.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg hover:scale-110 transition-transform"
                        style={{ backgroundColor: 'var(--color-primary)' + '20' }}
                        aria-label={`${member.name}'s GitHub`}
                      >
                        <Github size={20} style={{ color: 'var(--color-primary)' }} />
                      </a>
                    )}
                  </div>
                  <p style={{ color: 'var(--color-text)' }}>
                    {member.role}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Special Thanks Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-theme font-bold mb-8 text-center text-gradient">
              Special Thanks
            </h2>

            <div className="grid sm:grid-cols-2 gap-4">
              {specialThanks.map((thanks, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="p-4 rounded-lg"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                >
                  <p className="font-semibold" style={{ color: 'var(--color-primary)' }}>
                    {thanks.name}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    {thanks.role}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-theme font-bold mb-12 text-center text-gradient">
              Frequently Anticipated Questions
            </h2>

            <div className="space-y-6">
              {faqItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 rounded-xl"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                >
                  <p className="text-lg font-semibold mb-2" style={{ color: 'var(--color-primary)' }}>
                    Q: {item.q}
                  </p>
                  <p style={{ color: 'var(--color-text)' }}>
                    A: {item.a}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-8 rounded-xl"
            style={{
              background: 'var(--gradient-primary)',
              color: 'var(--color-bg)'
            }}
          >
            <h2 className="text-2xl md:text-3xl font-theme font-bold mb-4">
              Want to Get in Touch?
            </h2>
            <p className="mb-6">
              Send three crows to the top of the tallest mountain in the land. They will find us.
            </p>
            <p className="text-sm opacity-90">
              Or just join our Discord, I guess.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://github.com/theweebcoders"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                style={{
                  backgroundColor: 'var(--color-bg)',
                  color: 'var(--color-primary)'
                }}
              >
                <Github size={20} />
                <span>GitHub</span>
              </a>
              <a
                href="https://discord.gg/S7NcUdhKRD"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold border-2 transition-all hover:scale-105"
                style={{
                  borderColor: 'var(--color-bg)',
                  color: 'var(--color-bg)',
                  backgroundColor: 'transparent'
                }}
              >
                Discord
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
};

export default AboutPage;
